import { useEffect, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { getToken } from "../../utils/GetToken";
import { useTranslation } from "react-i18next";
import assignDataGridLanguage from "../../utils/AssignDatagridLanguage";
import { useSearchParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { GetAllPlansService } from "../../services/Plans/GetAllPlansService";
import { HeadersPlanList } from "../../utils/TableHeaders/PlansHeaders";
import { Delete } from "@mui/icons-material";
import { usePlan } from "../../context/PlanContext";

export default function PlanList({ currentTab }) {
  const { t } = useTranslation();
  const {
    isPlanDeleted,
    resetPlanStates,
    handlePlanDeleted,
    handleIsPlanDeleted,
    // isDeletePlanMessage,
  } = usePlan();
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [languageDatagrid, setLanguageDatagrid] = useState({});
  const [queryOptions, setQueryOptions] = useState({});
  const [query, setQuery] = useState(searchParams ? searchParams : {});
  const pageUrl = searchParams.get("page");
  const paginationUrl = searchParams.get("pagination");
  const fromParamUrl = searchParams.get("from");
  const toParamUrl = searchParams.get("to");

  const [pageFromUrl, setPageFromUrl] = useState(
    pageUrl !== null && pageUrl !== 0
      ? parseInt(searchParams.get("page"), 10)
      : 0
  );
  const [paginationFromUrl, setPaginationFromUrl] = useState(
    paginationUrl !== null && paginationUrl !== 0
      ? parseInt(searchParams.get("pagination"), 10)
      : 100
  );
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: pageFromUrl,
    pageSize: paginationFromUrl,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: pageFromUrl,
    pageSize: paginationFromUrl,
  });
  const currentLanguage = localStorage.getItem("i18nextLng");
  const [dateFrom, setDateFrom] = useState(
    fromParamUrl !== null ? dayjs(searchParams.get("from")) : null
  );
  const [dateTo, setDateTo] = useState(
    toParamUrl !== null ? dayjs(searchParams.get("to")) : null
  );
  const currentURL = window.location.href;

  const handlePageChange = (params) => {
    setPageState((old) => ({
      ...old,
      page: params.page,
      pageSize: params.pageSize,
    }));
    setPaginationModel((old) => ({
      ...old,
      page: params.page,
      pageSize: params.pageSize,
    }));
  };

  useEffect(() => {
    setLanguageDatagrid(assignDataGridLanguage(currentLanguage));
    extractParamsAndAssignState();
  }, [currentLanguage]);

  const onFilterChange = useCallback(
    (filterModel) => {
      setQueryOptions({ filterModel: { ...filterModel } });
    },
    [query]
  );

  useEffect(() => {
    setPaginationModel((old) => ({
      ...old,
      page: pageFromUrl,
      pageSize: paginationFromUrl,
    }));
  }, [pageFromUrl, paginationFromUrl, query]);

  useEffect(() => {
    searchParams.set("page", `${pageState.page}`);
    searchParams.set("pagination", `${pageState.pageSize}`);
    setSearchParams(searchParams);
  }, [pageState.page, pageState.pageSize]);

  useEffect(() => {
    setPageState((old) => ({
      ...old,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    }));
    getPlans();
  }, [pageState.page, pageState.pageSize, query, paginationModel, currentTab]);

  useEffect(() => {
    if (isPlanDeleted) {
      getPlans();
      setShowDeleteMessage(true);
      resetPlanStates();
    }
  }, [isPlanDeleted]);

  const getPlans = async () => {
    try {
      const token = await getToken();
      if (token) {
        setPageState((old) => ({ ...old, isLoading: true }));
        const currentPage = pageState.page + 1;
        const pagination = pageState.pageSize;
        const response = await GetAllPlansService(
          token,
          currentPage,
          query,
          pagination
        );
        const { data, links, meta } = response;
        console.log(data);
        if (Array.isArray(data) && data.length !== 0) {
          const mappedData = mapData(data);
          setPageState((old) => ({
            ...old,
            isLoading: false,
            data: mappedData,
            total: meta.total,
          }));
        } else {
          setPageState((old) => ({
            ...old,
            isLoading: false,
            data: [],
            total: 0,
          }));
        }
        return;
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  useEffect(() => {
    const query = queryOptions?.filterModel?.items[0];
    // console.log("Query with filterModel", query);
    if (query !== undefined) {
      const { field, value } = query;
      if (field !== undefined && value !== undefined) {
        if (field === "id") {
          setQuery(`reportId=${value}`);
          searchParams.set(`reportId`, `${value}`);
          resetPagination();
          setSearchParams(searchParams);
          return;
        } else {
          setQuery(`${field}=${value}`);
          searchParams.set(`${field}`, `${value}`);
          resetPagination();
          setSearchParams(searchParams);
          return;
        }
      } else {
        setQuery({});
        searchParams.delete(`${field}`);
        setSearchParams(searchParams);
      }
    }
  }, [queryOptions]);

  const mapData = (data) =>
    data.map((item) => {
      const mappedItem = {
        id: item.id,
        title: item.title,
        duration: item.duration,
        contacts: item.contacts,
        value: item.value,
        discount: item.discount,
        oldValue: item.oldValue,
        commissionable: item.commissionable,
      };
      return mappedItem;
    });

  const extractParamsAndAssignState = () => {
    const currentURL = window.location.href;
    const urlSearchParams = new URLSearchParams(currentURL.split("?")[1]);
    const paramsArray = [];

    urlSearchParams.forEach((value, key) => {
      paramsArray.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    });

    const state = paramsArray.join("&");
    setQuery(state);

    return state;
  };

  useEffect(() => {
    const formattedDateFrom = dayjs(dateFrom).format("YYYY-MM-DD");
    console.log("inside date from");
    if (formattedDateFrom !== null && dayjs(formattedDateFrom).isValid()) {
      searchParams.set(`from`, `${formattedDateFrom}`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    } else {
      searchParams.delete(`from`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    }
  }, [dateFrom]);

  useEffect(() => {
    const formattedDateTo = dayjs(dateTo).format("YYYY-MM-DD");

    if (formattedDateTo !== null && dayjs(formattedDateTo).isValid()) {
      searchParams.set(`to`, `${formattedDateTo}`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    } else {
      searchParams.delete(`to`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    }
  }, [dateTo]);

  const resetPagination = () => {
    searchParams.set("page", 0);
    searchParams.set("pagination", 100);
    setPageFromUrl(parseInt(searchParams.get("page"), 10));
    setPaginationFromUrl(parseInt(searchParams.get("pagination"), 10));
  };

  const handleClearDateFrom = (event) => {
    event.stopPropagation();
    setDateFrom(null);
  };

  const handleClearDateTo = (event) => {
    event.stopPropagation();
    setDateTo(null);
  };

  const handleCloseDeletePlanMessage = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setShowDeleteMessage(false);
  };

  return (
    <>
      <DataGrid
        rows={pageState.data}
        columns={HeadersPlanList()}
        localeText={languageDatagrid}
        rowCount={pageState.total}
        loading={pageState.isLoading}
        pagination
        page={pageState.page}
        filterMode="server"
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(params) => handlePageChange(params)}
        onFilterModelChange={onFilterChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        slots={{
          toolbar: GridToolbar,
        }}
        sx={{
          boxShadow: "0 1px 12px rgba(149, 157, 165, 0.2)",
          borderRadius: "25px",
          borderColor: "transparent",
          backgroundColor: "white",
          paddingX: "1.5rem",
          "& .MuiDataGrid-cell": {
            borderColor: "gray.light",
          },
          // "& .MuiTablePagination-selectLabel": {
          //   color: "primary.main",
          // },
          // "& .MuiTablePagination-select": {
          //   fontWeight: 700,
          //   color: "primary.main",
          // },
          // "& .MuiTablePagination-displayedRows": {
          //   color: "primary.main",
          // },
          // "& .MuiTablePagination-actions .MuiButtonBase-root": {
          //   padding: "2px",
          //   marginInline: "5px",
          //   bgcolor: "primary.main",
          //   color: "white",
          // },
          "& .MuiDataGrid-cell:hover": {
            color: "secondary.main",
          },
          // "& .MuiDataGrid-columnHeaderTitle": {
          //   color: "primary.main",
          // },
          "& .super-app-theme--header": {
            backgroundColor: "white",
          },
        }}
      />
      <Snackbar
        open={showDeleteMessage}
        autoHideDuration={3000}
        onClose={handleCloseDeletePlanMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">
          {t("createDeletePlan.success.message")}
        </Alert>
      </Snackbar>
    </>
  );
}
